import { useDebounce } from 'use-debounce';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BrandIndustry,
  CashbackFilter,
  CoinTransactionParams,
  CustomerScopeType,
  DateRangeParams,
  FlaggedOrdersParams,
  FoodItemParams,
  PaginationParams,
  PilotsParams,
  ResolvedResponse,
} from '../types';
import type {
  CompanyListParams,
  CompanyListResponse,
  CompanyStatus,
  CompanySummary,
} from '../types';
import type {
  IScheduledPaymentRequest,
  IScheduledPaymentRequestListParams,
  IScheduledPaymentRequestListResponse,
} from '../types';
import {
  brandApi,
  categoriesApi,
  companiesApi,
  couponsApi,
  loyaltyApi,
  mealsApi,
  pilotsApi,
  ordersApi,
  kitchensApi,
  getAllDeliveryRates,
  usersApi,
  tenantApi,
  scheduledPaymentRequestsApi,
} from '../api';
import { dashboardApi, DashboardParams } from '../api/dashboard.api';
import {
  CreateProductGroupPayload,
  ICategory,
  ProductGroup,
} from '../types/categories.types';
import {
  get_product_groups,
  get_subcategories_with_filters,
  get_subcategory_food_items,
  get_departments,
  list_variant_groups,
  list_all_products,
  get_products_by_variant_groups,
} from '../api/categories.api';
import { Department } from '../types/categories.types';

export const useFetch = () => {
  const queryClient = useQueryClient();
  /// foooood
  const useFetchFoodItem = <T>(
    id?: string,
    params?: FoodItemParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['food-items', id, params],
      () => mealsApi.listAllFoodItems(id, params) as T,
      options,
    );

  const useFetchAllFoodItems = <T>(params?: FoodItemParams, options?: any) =>
    useQuery<T>(
      ['all-food-items', params],
      () => mealsApi.getAllFoodItems(params) as T,
      options,
    );

  const useDeleteFoodItems = () => {
    return useMutation<any, unknown, { payload: string[] }>(
      // The mutation function receives our params object and transforms it for the API
      ({ payload }) => {
        // Transform the payload from string[] to { ids: string[] } for the API
        const apiPayload = { ids: payload };
        return mealsApi.deleteFoodItems(apiPayload);
      },
      {
        // Optional: Refresh the table data after a successful delete
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['all-food-items'] });
          console.log('Successfully deleted items');
        },
        onError: (error: any) => {
          console.error('Delete failed:', error);
        },
      },
    );
  };

  const useSetFoodItemOptionOnly = () => {
    return useMutation<any, unknown, { ids: string[] }>(
      ({ ids }) => {
        if (ids.length === 1) {
          return mealsApi.setFoodItemOptionOnly(ids[0], { optionOnly: true });
        }
        return mealsApi.setBulkFoodItemOptionOnly({ ids, optionOnly: true });
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['all-food-items'] });
        },
      },
    );
  };

  const useFetchFoodGroup = <T>(
    id?: string,
    params?: FoodItemParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['food-group', id, params],
      () => mealsApi.getFoodGroups(id, params) as T,
      options,
    );

  const useFetchOptions = (params?: FoodItemParams, options?: any) =>
    useQuery(['options', params], () => mealsApi.getOptions(params), options);

  const useFetchFoodItemVariants = <T>(id?: string, options?: any) =>
    useQuery<T>(
      ['food-item-variants', id],
      () => mealsApi.getFoodItemVariants(id!) as T,
      { enabled: !!id, ...options },
    );

  const useFetchMasterProductParent = <T>(masterId?: string, options?: any) =>
    useQuery<T>(
      ['master-product-parent', masterId],
      () => mealsApi.getMasterProductParent(masterId!) as T,
      { enabled: !!masterId, ...options },
    );
  const useFetchMasterProductForEdit = <T>(masterId?: string, options?: any) =>
    useQuery<T>(
      ['master-product-for-edit', masterId],
      () => mealsApi.getMasterProductForEdit(masterId!) as T,
      { enabled: !!masterId, ...options },
    );

  /// brand
  const useFetchBrand = <T>(slug?: string, options?: any) =>
    useQuery<T>(['brands', slug], () => brandApi.getBrand(slug) as T, options);

  const useFetchAllBrands = <T>(
    params?: { page?: number; count?: number },
    options?: any,
  ) =>
    useQuery<T>(
      ['all-brands', params],
      () => brandApi.getAllBrands(params) as T,
      options,
    );

  const useFetchBrandIndustries = (options?: any) =>
    useQuery<BrandIndustry[]>(
      ['brand-industries'],
      () => brandApi.getBrandIndustries(),
      { staleTime: 1000 * 60 * 60, ...options },
    );

  /// category
  const useFetchCategory = <T = ICategory | ICategory[]>(
    slug?: string,
    options?: any,
  ) =>
    useQuery<T>(
      ['category', slug],
      () => categoriesApi.getCategory(slug) as unknown as T,
      options,
    );

  const useFetchBrandCategories = <T>(
    brandId?: string | number | null,
    options?: any,
  ) =>
    useQuery<T>(
      ['brand-categories', brandId],
      () => categoriesApi.getBrandCategories(brandId!) as T,
      { enabled: !!brandId, ...options },
    );

  const useFetchAllPilots = (
    slug?: string,
    params?: PilotsParams,
    options?: any,
    useAdmin?: boolean,
  ) =>
    useQuery(
      ['pilots', params, slug],
      () => pilotsApi.getAllPilots(slug, params, useAdmin),
      options,
    );

  const useSearchPilots = (
    searchTerm: string,
    options?: {
      enabled?: boolean;
      debounceMs?: number;
      kitchenSlug?: string;
      isPublic?: boolean;
    },
  ) => {
    const debounceMs = options?.debounceMs ?? 1000;
    const [debouncedSearch] = useDebounce(searchTerm, debounceMs);

    const query = useQuery(
      [
        'pilots-search',
        options?.kitchenSlug,
        options?.isPublic,
        debouncedSearch,
      ],
      () =>
        pilotsApi.getAllPilots(options?.kitchenSlug ?? '', {
          search: debouncedSearch,
          ...(options?.isPublic && { isPublic: true }),
        }),
      {
        enabled: options?.enabled !== false && debouncedSearch.length > 0,
        keepPreviousData: true,
      },
    );

    return {
      ...query,
      isDebouncing: searchTerm !== debouncedSearch,
    };
  };

  const useFetchPilotSummary = (options?: any) =>
    useQuery(['pilots-summary'], () => pilotsApi.getPilotsSummary(), options);

  // coupons
  const useFetchCashback = (options?: any) =>
    useQuery(['cashback'], () => couponsApi.getActiveCashback(), options);

  const useFetchCashbackTransactions = (
    params?: CashbackFilter,
    options?: any,
  ) =>
    useQuery(
      ['cashback-transactions'],
      () => couponsApi.getCashbackTransactions(params),
      options,
    );

  const useFetchLoyaltyDashboard = (params?: DateRangeParams, options?: any) =>
    useQuery(
      ['loyalty-dashboard', params],
      () => loyaltyApi.getLoyaltyDashboard(params),
      options,
    );

  const useFetchCoinTransactions = (
    params?: CoinTransactionParams,
    options?: any,
  ) =>
    useQuery(
      ['coin-transactions', params],
      () => loyaltyApi.listCoinTransaction(params),
      options,
    );

  const useFetchLoyaltyTiers = (params?: PaginationParams, options?: any) =>
    useQuery(
      ['loyalty-tiers', params],
      () => loyaltyApi.getAllTier(params),
      options,
    );

  const useFetchAllActions = (params?: PaginationParams, options?: any) =>
    useQuery(
      ['loyalty-actions', params],
      () => loyaltyApi.getAllActions(params),
      options,
    );

  const useFetchBudgets = (options?: any) =>
    useQuery(['budgets'], () => couponsApi.getAllBudgets(), options);

  const useFetchFlaggedOrders = (params?: FlaggedOrdersParams, options?: any) =>
    useQuery(
      ['flagged-orders', params],
      () => ordersApi.getFlaggedOrders(params),
      options,
    );

  const useFetchKitchenRequests = (params?: PaginationParams, options?: any) =>
    useQuery(
      ['kitchen-requests', params],
      () => kitchensApi.getKitchenRequests(params),
      options,
    );

  // Delivery rates
  const useFetchDeliveryRates = (options?: any) =>
    useQuery(['delivery-rates'], () => getAllDeliveryRates(), options);

  const useFetchKitchenDeliveryConfig = (options?: any) =>
    useQuery(
      ['kitchen-delivery-config'],
      () => kitchensApi.getAllKitchenDeliveryConfig(),
      options,
    );

  const useFetchCustomerSegment = (
    scope: CustomerScopeType,
    id: number,
    options?: any,
  ) => {
    return useQuery(
      ['fetch-customer-segment'],
      async () => {
        const response = await usersApi.getCustomerSegment(scope, id);
        return response.data.data; // Gets the actual customer segment data
      },
      options,
    );
  };

  const useFetchSubcategories = (params?: any, options?: any) =>
    useQuery(
      ['subcategories', params],
      () => get_subcategories_with_filters(params),
      options,
    );

  const useFetchSubcategoryFoodItems = (id?: number, options?: any) =>
    useQuery(
      ['subcategory-food-items', id],
      () => get_subcategory_food_items(id!),
      { enabled: !!id, ...options },
    );

  const useFetchDepartments = (
    params?: { page?: number; count?: number },
    options?: any,
  ) =>
    useQuery<ResolvedResponse<Department[]>, Error>(
      ['departments', params],
      () => get_departments(params),
      { staleTime: 1000 * 60 * 5, ...options },
    );
  // wallet
  const useFetchUserWallet = (email: string, options?: any) =>
    useQuery(
      ['user-wallet', email],
      () => usersApi.getUserWallet(email),
      options,
    );

  // tenant
  const useFetchTenantTheme = (options?: any) =>
    useQuery(['tenant-theme'], () => tenantApi.getTenantTheme(), options);

  const useFetchCurrencyPricing = (options?: any) =>
    useQuery(
      ['currency-pricing'],
      () => tenantApi.getCurrencyPricing(),
      options,
    );

  // Department, Category, Subcategory, Collection
  const useFetchDepartmentById = <T>(id?: string, options?: any) =>
    useQuery<T>(
      ['department', id],
      () => categoriesApi.getDepartmentById(id!) as T,
      { enabled: !!id, ...options },
    );

  const useFetchCategoryById = <T = ICategory>(
    categoryId?: number,
    options?: any,
  ) =>
    useQuery(
      ['get-category-by-id', categoryId],
      () => categoriesApi.getCategoryById(categoryId!) as T,
      { enabled: !!categoryId, ...options },
    );

  const useFetchSubcategoryById = <T>(subcategoryId?: number, options?: any) =>
    useQuery<T>(
      ['subcategory', subcategoryId],
      () => categoriesApi.getSubcategoryById(subcategoryId!) as T,
      { enabled: !!subcategoryId, ...options },
    );

  const useFetchCollections = (
    params?: { name?: string; page?: number; count?: number },
    options?: any,
  ) =>
    useQuery<ResolvedResponse<ProductGroup[]>>(
      ['collections', params],
      () => categoriesApi.getCollections(params),
      options,
    );

  const useFetchAllCategories = (params?: any, options?: any) =>
    useQuery(
      ['all-categories', params],
      () => categoriesApi.getAllCategories(params),
      options,
    );

  const useFetchCollectionById = <T>(collectionId?: string, options?: any) =>
    useQuery<T>(
      ['collections', collectionId],
      () => categoriesApi.getCollectionById(collectionId!) as T,
      { enabled: !!collectionId, ...options },
    );

  const useFetchProductGroups = (
    params?: CreateProductGroupPayload,
    options?: any,
  ) =>
    useQuery(
      ['product-groups', params],
      () => get_product_groups(params),
      options,
    );

  const useFetchAllVariantGroups = (options?: any) =>
    useQuery(['variant-groups-all'], () => list_variant_groups(), options);

  const useFetchAllProducts = (options?: any) =>
    useQuery(['all-products'], () => list_all_products(), options);

  const useFetchProductsByVariantGroups = (
    groupIds?: string[],
    options?: any,
  ) =>
    useQuery(
      ['products-by-variant-groups', groupIds],
      () => get_products_by_variant_groups(groupIds || []),
      { enabled: !!groupIds && groupIds.length > 0, ...options },
    );

  // Dashboard
  const useFetchOrderSummary = <T = any>(
    params?: DashboardParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['order-summary', params],
      () => dashboardApi.fetchOrderSummary(params) as T,
      { staleTime: 1000 * 60 * 2, ...options },
    );

  const useFetchRevenueSummary = <T = any>(
    params?: DashboardParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['revenue-summary', params],
      () => dashboardApi.fetchRevenueSummary(params) as T,
      { staleTime: 1000 * 60 * 2, ...options },
    );

  const useFetchTopBrands = <T = any>(
    params?: DashboardParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['top-brands', params],
      () => dashboardApi.fetchTopBrands(params) as T,
      { staleTime: 1000 * 60 * 2, ...options },
    );

  const useFetchTopMeals = <T = any>(
    params?: DashboardParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['top-meals', params],
      () => dashboardApi.fetchTopMeals(params) as T,
      { staleTime: 1000 * 60 * 2, ...options },
    );

  const useFetchTopCustomers = <T = any>(
    params?: DashboardParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['top-customers', params],
      () => dashboardApi.fetchTopCustomers(params) as T,
      { staleTime: 1000 * 60 * 2, ...options },
    );

  const useFetchTopUsers = <T = any>(
    params?: DashboardParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['top-users', params],
      () => dashboardApi.fetchTopUsers(params) as T,
      { staleTime: 1000 * 60 * 2, ...options },
    );

  const useFetchRecentOrders = <T = any>(
    params?: DashboardParams,
    options?: any,
  ) =>
    useQuery<T>(
      ['recent-orders', params],
      () => dashboardApi.fetchRecentOrders(params) as T,
      { staleTime: 1000 * 60 * 2, ...options },
    );

  // Companies (platform admin)
  const useFetchCompanies = (params: CompanyListParams, options?: any) =>
    useQuery<CompanyListResponse>(
      ['companies', params],
      () => companiesApi.listCompanies(params),
      { keepPreviousData: true, ...options },
    );

  const useFetchCompanyCount = (status: CompanyStatus, options?: any) =>
    useQuery<CompanyListResponse, unknown, number>(
      ['companies', 'count', status],
      () => companiesApi.listCompanies({ status, limit: 1, page: 1 }),
      {
        select: (r) => r.pagination?.totalRecords ?? 0,
        ...options,
      },
    );

  const useChangeCompanyStatus = () =>
    useMutation<CompanySummary, unknown, { id: string; status: CompanyStatus }>(
      ({ id, status }) => companiesApi.changeCompanyStatus(id, status),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['companies'] });
        },
      },
    );

  // Scheduled payment requests (platform admin)
  const useFetchScheduledPaymentRequests = (
    params: IScheduledPaymentRequestListParams,
    options?: any,
  ) =>
    useQuery<IScheduledPaymentRequestListResponse>(
      ['scheduled-payment-requests', params],
      () => scheduledPaymentRequestsApi.list(params),
      { keepPreviousData: true, ...options },
    );

  const useFetchPendingScheduledPaymentRequestsCount = (options?: any) =>
    useQuery<IScheduledPaymentRequestListResponse, unknown, number>(
      ['scheduled-payment-requests', 'count', 'PENDING'],
      () =>
        scheduledPaymentRequestsApi.list({
          status: 'PENDING',
          limit: 1,
          page: 1,
        }),
      {
        select: (r) => r.pagination?.totalRecords ?? 0,
        ...options,
      },
    );

  const useApproveScheduledPaymentRequest = () =>
    useMutation<IScheduledPaymentRequest, unknown, { id: string }>(
      ({ id }) => scheduledPaymentRequestsApi.approve(id),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['scheduled-payment-requests'],
          });
        },
      },
    );

  const useDeclineScheduledPaymentRequest = () =>
    useMutation<
      IScheduledPaymentRequest,
      unknown,
      { id: string; reason?: string }
    >(
      ({ id, reason }) => scheduledPaymentRequestsApi.decline(id, reason),
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['scheduled-payment-requests'],
          });
        },
      },
    );

  return {
    useFetchOrderSummary,
    useFetchRevenueSummary,
    useFetchTopBrands,
    useFetchTopMeals,
    useFetchTopCustomers,
    useFetchTopUsers,
    useFetchRecentOrders,
    useFetchBrand,
    useFetchAllBrands,
    useFetchBrandIndustries,
    useFetchOptions,
    useFetchBudgets,
    useFetchFoodItem,
    useFetchAllFoodItems,
    useDeleteFoodItems,
    useSetFoodItemOptionOnly,
    useFetchFoodItemVariants,
    useFetchMasterProductParent,
    useFetchMasterProductForEdit,
    useFetchCategory,
    useFetchBrandCategories,
    useFetchCashback,
    useFetchFoodGroup,
    useFetchAllPilots,
    useSearchPilots,
    useFetchAllActions,
    useFetchUserWallet,
    useFetchTenantTheme,
    useFetchCurrencyPricing,
    useFetchPilotSummary,
    useFetchLoyaltyTiers,
    useFetchDeliveryRates,
    useFetchFlaggedOrders,
    useFetchKitchenRequests,
    useFetchCustomerSegment,
    useFetchCoinTransactions,
    useFetchLoyaltyDashboard,
    useFetchCashbackTransactions,
    useFetchKitchenDeliveryConfig,
    useFetchDepartmentById,
    useFetchCategoryById,
    useFetchSubcategoryById,
    useFetchCollectionById,
    useFetchCollections,
    useFetchProductGroups,
    useFetchAllVariantGroups,
    useFetchAllProducts,
    useFetchProductsByVariantGroups,
    useFetchSubcategories,
    useFetchSubcategoryFoodItems,
    useFetchDepartments,
    useFetchAllCategories,
    useFetchCompanies,
    useFetchCompanyCount,
    useChangeCompanyStatus,
    useFetchScheduledPaymentRequests,
    useFetchPendingScheduledPaymentRequestsCount,
    useApproveScheduledPaymentRequest,
    useDeclineScheduledPaymentRequest,
  };
};
